import React, { createContext, useContext, useEffect, useState } from 'react';
import { clearSupabaseAuthStorage, supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const restoreSession = async () => {
      try {
        const response = await fetch('/api/auth/me', { credentials: 'include' });
        const result = await response.json().catch(() => null);
        if (!active) return;
        setUser(response.ok && result?.success ? result.data : null);
        setSession(null);
      } catch {
        if (active) {
          setUser(null);
          setSession(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    void restoreSession();
    return () => {
      active = false;
    };
  }, []);

  const getSessionId = () => {
    if (typeof window === 'undefined') return '';
    return (
      window.localStorage.getItem('cms_session_id') ||
      window.localStorage.getItem('session_id') ||
      ''
    ).trim();
  };

  const signUp = async (email: string, password: string) => {
    try {
      const sessionId = getSessionId();
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: JSON.stringify({ email, password }),
      });
      const result = await res.json();
      if (!result.success) {
        return { error: new Error(result.error?.message || 'Registration failed') };
      }

      const user = result?.data?.user ?? result?.data?.session?.user ?? null;
      if (user) {
        setUser(user);
        setSession(null);
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const sessionId = getSessionId();
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: JSON.stringify({ email, password }),
      });
      const result = await res.json();
      if (!result.success) {
        return { error: new Error(result.error?.message || 'Login failed') };
      }

      const user = result?.data?.user ?? result?.data?.session?.user ?? null;
      if (user) {
        setUser(user);
        setSession(null);
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    // 先通知服务端清 Cookie；失败也不应阻止前端立即退出
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});

    clearSupabaseAuthStorage();
    setSession(null);
    setUser(null);
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
